<script setup lang="ts">
import { just, nothing } from "../lib/maybe";
import { computed } from "vue";
import type {
  ZwCopyrightCredit,
  ZwCopyrightPage,
  ZwDedicationPage,
} from "../lib/zwConfig";
import ZwButton from "./ui/ZwButton.vue";
import ZwCheckbox from "./ui/ZwCheckbox.vue";
import ZwInput from "./ui/ZwInput.vue";
import ZwTextarea from "./ui/ZwTextarea.vue";

const copyright = defineModel<ZwCopyrightPage>("copyright", {
  required: true,
});
const dedication = defineModel<ZwDedicationPage>("dedication", {
  required: true,
});

const props = defineProps<{
  /** From Metadata — copyright placeholders derive from these. */
  metaPubDate: string;
  metaPublisher: string;
  metaAuthor: string;
}>();

const editionPlaceholder = computed(() => {
  const date = props.metaPubDate.trim();
  return date.length > 0
    ? `First paperback edition ${date}`
    : "First paperback edition 2026";
});

const publishedByPlaceholder = computed(() => {
  const publisher = props.metaPublisher.trim();
  if (publisher.length > 0) return publisher;
  const author = props.metaAuthor.trim();
  if (author.length > 0) return author;
  return "Author or publisher";
});

/** Replace the whole page so TEA `shallowRef` parents see a new value. */
function patchCopyright(patch: Partial<ZwCopyrightPage>): void {
  copyright.value = { ...copyright.value, ...patch };
}

function patchDedication(patch: Partial<ZwDedicationPage>): void {
  dedication.value = { ...dedication.value, ...patch };
}

function addCredit(): void {
  patchCopyright({
    credits: [...copyright.value.credits, { role: "", name: "" }],
  });
}

function updateCredit(
  index: number,
  key: keyof ZwCopyrightCredit,
  value: string,
): void {
  const credits = copyright.value.credits.map((credit, i) =>
    i === index ? { ...credit, [key]: value } : credit,
  );
  patchCopyright({ credits });
}

function removeCredit(index: number): void {
  patchCopyright({
    credits: copyright.value.credits.filter((_, i) => i !== index),
  });
}
</script>

<template>
  <section
    class="project-settings-group"
    aria-labelledby="settings-copyright-heading"
  >
    <h3 id="settings-copyright-heading" class="project-settings-subhead">
      Copyright page
    </h3>

    <ZwCheckbox
      :model-value="copyright.enabled"
      @update:model-value="patchCopyright({ enabled: $event })"
    >
      Include a copyright page in exports
    </ZwCheckbox>

    <div v-if="copyright.enabled" class="project-settings-copyright-fields">
      <ZwCheckbox
        :model-value="copyright.includeRightsParagraph"
        @update:model-value="patchCopyright({ includeRightsParagraph: $event })"
      >
        Include “all rights reserved” paragraph
      </ZwCheckbox>
      <ZwCheckbox
        :model-value="copyright.includeAiRestriction"
        @update:model-value="patchCopyright({ includeAiRestriction: $event })"
      >
        Include a restriction on AI training
      </ZwCheckbox>

      <ZwInput
        :id="nothing"
        :model-value="copyright.authorUrl"
        :label="just('Author / publisher URL')"
        placeholder="https://example.com"
        autocomplete="url"
        spellcheck="false"
        @update:model-value="patchCopyright({ authorUrl: $event })"
      />
      <ZwInput
        :id="nothing"
        :model-value="copyright.editionLine"
        :label="just('Edition line')"
        :placeholder="editionPlaceholder"
        autocomplete="off"
        @update:model-value="patchCopyright({ editionLine: $event })"
      />
      <ZwInput
        :id="nothing"
        :model-value="copyright.publishedBy"
        :label="just('Published by')"
        :placeholder="publishedByPlaceholder"
        autocomplete="organization"
        @update:model-value="patchCopyright({ publishedBy: $event })"
      />

      <div class="project-settings-credits">
        <div class="project-settings-credits-head">
          <h4 class="project-settings-label">Credits</h4>
          <ZwButton size="sm" @click="addCredit">Add credit</ZwButton>
        </div>
        <p class="project-settings-hint">
          Credit contributors as “Role © Name”, for example “Cover art © Jane
          Smith”.
        </p>
        <div
          v-for="(credit, index) in copyright.credits"
          :key="index"
          class="project-settings-credit-row"
        >
          <ZwInput
            :id="nothing"
            :model-value="credit.role"
            :label="just('Role')"
            placeholder="Cover art"
            autocomplete="off"
            @update:model-value="updateCredit(index, 'role', $event)"
          />
          <ZwInput
            :id="nothing"
            :model-value="credit.name"
            :label="just('Name')"
            placeholder="Artist name"
            autocomplete="name"
            @update:model-value="updateCredit(index, 'name', $event)"
          />
          <ZwButton
            size="sm"
            variant="danger"
            class="project-settings-credit-remove"
            @click="removeCredit(index)"
          >
            Remove
          </ZwButton>
        </div>
        <p v-if="copyright.credits.length === 0" class="project-settings-hint">
          No credits yet.
        </p>
      </div>

      <ZwTextarea
        :id="nothing"
        :model-value="copyright.additionalText"
        :label="just('Additional text')"
        placeholder="Printer’s note or anything else for this page"
        @update:model-value="patchCopyright({ additionalText: $event })"
      />
    </div>
  </section>

  <section
    class="project-settings-group"
    aria-labelledby="settings-dedication-heading"
  >
    <h3 id="settings-dedication-heading" class="project-settings-subhead">
      Dedication page
    </h3>
    <ZwCheckbox
      :model-value="dedication.enabled"
      @update:model-value="patchDedication({ enabled: $event })"
    >
      Include a dedication page in exports
    </ZwCheckbox>
    <div v-if="dedication.enabled">
      <ZwTextarea
        :id="nothing"
        :model-value="dedication.text"
        :label="just('Dedication')"
        @update:model-value="patchDedication({ text: $event })"
      />
    </div>
  </section>
</template>
